import { Sparkles } from "lucide-react";
import { Heading } from "@/components/ui/heading";
import { DownloadButtons } from "@/components/ui/download-buttons";
import { useState, useEffect } from "react";
import { useTheme } from "@/components/themeProvider";
import { useTranslation } from "@/hooks/useTranslation";
import { motion } from "framer-motion";

export const ProgressCTA = () => {
  const [imageSrc, setImageSrc] = useState("/home/cta_light.webp");
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const { t } = useTranslation();

  useEffect(() => {
    setImageSrc(isDark ? "/home/cta_dark.webp" : "/home/cta_light.webp");
  }, [isDark]);

  return (
    <section
      id="cta-section"
      data-testid="cta-section"
      className="col-span-12 lg:col-span-10 lg:col-start-2 bg-white dark:bg-black grid grid-cols-1 lg:grid-cols-2 shadow-sm rounded-3xl relative isolate overflow-hidden bg-muted px-6 pt-16 sm:px-8 md:px-10 lg:px-12 lg:flex lg:pt-0"
    >
      <svg
        viewBox="0 0 1024 1024"
        className="absolute top-0 left-1/2 -translate-x-1/2 lg:top-1/2 -z-10 size-[64rem] lg:-translate-y-1/2 [mask-image:radial-gradient(closest-side,white,transparent)] sm:left-full sm:-ml-80 lg:left-1/2 lg:ml-0 lg:-translate-x-1/2 lg:translate-y-0 opacity-50 md:opacity-100"
        aria-hidden="true"
      >
        <circle
          cx="512"
          cy="512"
          r="512"
          fill="url(#759c1415-0410-454c-8f7c-9a820de03641)"
          fillOpacity="0.7"
        />
        <defs>
          <radialGradient id="759c1415-0410-454c-8f7c-9a820de03641">
            <stop stopColor="#2594ef" />
            <stop offset="1" stopColor="#2594ef" />
          </radialGradient>
        </defs>
      </svg>
      <div className="mx-auto max-w-md text-center lg:mx-0 lg:flex-auto lg:py-16 lg:text-left">
        <div className="flex flex-col items-center lg:items-start gap-4 sm:gap-6">
          <div className="rounded-full bg-amber-500/10 p-3">
            <Sparkles className="size-6 sm:h-8 sm:w-8 text-amber-500" />
          </div>
          <div className="space-y-2 w-full">
            <Heading level="h2" size="h4">
              {t("cta.title")}
            </Heading>

            <p className="text-lg/8 text-pretty text-muted-foreground">{t("cta.description")}</p>
          </div>
        </div>

        <DownloadButtons className="mt-10" />
      </div>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.2, duration: 0.8, ease: "easeOut" }}
        className="mt-auto w-full min-h-[450px] max-h-[450px] select-none"
      >
        <motion.div
          whileTap={{ scale: 1.1 }}
          whileHover={{ scale: 1.02 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className="flex justify-center"
        >
          <img
            src={imageSrc}
            alt={t("cta.image_alt")}
            className="object-contain w-full min-w-[350px] min-h-[600px] max-h-[880px] mt-6 pointer-events-none"
            loading="lazy"
          />
        </motion.div>
      </motion.div>
    </section>
  );
};
